import React, { useState, useEffect, useRef } from 'react';
import Tooltip from 'rc-tooltip';
import '../../style/customRCTooltip.css';
import VerifiedBadgeIcon from '../../atoms/icons/VerifiedBadgeIcon';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setCloseTooltip } from '../../actions/profileActions';
import IDVBadge from '../../atoms/icons/IDVBadge';
import { isDarkMode } from '../../utils/helpers';
import SVBadge from '../../atoms/icons/SVBadge';
import styled, { CSSObject } from '@emotion/styled';
import breakpoints from 'theme/material/breakpoints';

const VerifiedBadgeStyle = styled('div')(
  (): CSSObject => ({
    position: 'relative',
    top: -4,
    display: 'inline-flex',
    visibility: 'visible',
    justifyContent: 'flex-end',
    opacity: 1,
    [breakpoints.down(768.05)]: {
      top: -2,
    },
  }),
  ({ customStyle }: { customStyle?: React.CSSProperties }) => ({
    ...customStyle,
  })
);

const TooltipContentContainer = styled('div')({
  textAlign: 'center',
  lineHeight: 'normal',
});

const BadgeText = styled('div')({
  fontSize: '11px',
  fontWeight: 400,
  lineHeight: '14px',
});

interface IVerifiedBadgeTooltipProps {
  showToolTip: boolean;
  isIdVerified: boolean;
  isSelfieVerified: boolean;
  customVerifiedBadgeStyle?: React.CSSProperties;
  customStyle?: React.CSSProperties;
}

const RcTooltip = Tooltip as unknown as React.FC<any>;

const VerifiedBadgeTooltip = ({
  showToolTip,
  isIdVerified,
  isSelfieVerified,
  customVerifiedBadgeStyle,
  customStyle,
}: IVerifiedBadgeTooltipProps): React.ReactElement => {
  const [allowTooltipClick, setAllowTooltipClick] = useState(showToolTip);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const dispatch = useDispatch();
  const { isMobile } = useMediaBreakpoints();
  const iconRef = useRef<HTMLDivElement>(null);

  const { closeTooltip } = useSelector((state: RootState) => state.profile);

  useEffect(() => {
    if (showToolTip) {
      setAllowTooltipClick(true);
    }
  }, [showToolTip]);

  useEffect(() => {
    if (closeTooltip) {
      setIsTooltipVisible(false);
      setTimeout(() => {
        dispatch(setCloseTooltip(false));
      }, 1000);
    }
  }, [closeTooltip]);

  // Hide tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobile && iconRef.current && !iconRef.current.contains(event.target as Node)) {
        setIsTooltipVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      setIsTooltipVisible(false);
    };
  }, [isMobile]);

  const handleTooltipClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (allowTooltipClick && isMobile) {
      setIsTooltipVisible(prev => !prev);
    }
  };

  const tooltipContent = (
    <TooltipContentContainer>
      <div>
        {isIdVerified ? (
          <IDVBadge isDarkMode={isDarkMode()} />
        ) : isSelfieVerified ? (
          <SVBadge isDarkMode={isDarkMode()} />
        ) : null}
      </div>
      <BadgeText>
        {isIdVerified ? (
          <>
            This member is{' '}
            <strong style={{ fontWeight: 700 }}>
              <br />
              ID Verified
            </strong>
          </>
        ) : isSelfieVerified ? (
          <>
            This member is{' '}
            <strong style={{ fontWeight: 700 }}>
              <br />
              Verified by Selfie
            </strong>
          </>
        ) : null}
      </BadgeText>
    </TooltipContentContainer>
  );

  return (
    <div
      style={{ display: 'inline-block', cursor: 'pointer', height: '16px' }}
      ref={iconRef}
      onMouseEnter={() => allowTooltipClick && !isMobile && setIsTooltipVisible(true)}
      onMouseLeave={() => {
        !isMobile && setIsTooltipVisible(false);
      }}
      onClick={handleTooltipClick}
      id="badge-tooltip"
    >
      <RcTooltip
        visible={isTooltipVisible}
        placement="top"
        arrowContent={<div className="rc-tooltip-arrow-inner" />}
        overlay={tooltipContent}
        overlayStyle={{
          maxWidth: '119px',
          zIndex: 11000,
        }}
        destroyTooltipOnHide
        overlayClassName={`badge-rc-tooltip ${isDarkMode() ? 'badge-rc-tooltip-dark' : ''}`}
      >
        <VerifiedBadgeStyle customStyle={customVerifiedBadgeStyle}>
          <VerifiedBadgeIcon
            verifiedColor={isIdVerified ? '#60ABE1' : isSelfieVerified ? '#E2C92D' : ''}
            style={customStyle}
          />
        </VerifiedBadgeStyle>
      </RcTooltip>
    </div>
  );
};

export default VerifiedBadgeTooltip;
